import React, { useEffect, useState, useRef } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { Modal, Button } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import Spinner from "../components/Common/Spinner";
import {
  addMentor,
  deleteAdmin,
  deleteMentor,
  getMentor,
  updateAdminStatus,
  updateMentor,

} from "../../services/Admin/adminServices";
import debounce from "lodash/debounce";
import Pagination from "../components/Pagination";
import { uploadFileToS3 } from "../../../lib/S3Bucket";

const Mentor = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  // Cleaned up unused state (preview, showPassword)
  const [imagePreview, setImagePreview] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [date, setDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState({});
  const [sortBy, setSortBy] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef(null);

  const svg1 = (
    <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect x="0" y="0" width="24" height="24"></rect>
        <circle fill="#000000" cx="12" cy="5" r="2"></circle>
        <circle fill="#000000" cx="12" cy="12" r="2"></circle>
        <circle fill="#000000" cx="12" cy="19" r="2"></circle>
      </g>
    </svg>
  );

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await getMentor(currentPage, itemsPerPage, debouncedSearch);
      setAdmins(response?.data?.data?.mentors || []);
      setTotalItems(response?.data?.data?.total);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [currentPage, debouncedSearch, sortBy, endDate]);

  useEffect(() => {
    setCurrentPage(0);
    setEndDate(null);
    setDate(null);
    const handler = debounce(() => {
      setDebouncedSearch(search);
    }, 500);

    handler();

    return () => {
      handler.cancel();
    };
  }, [search]);

  useEffect(() => {
    if (selectedAdmin?.image) {
      setImagePreview(selectedAdmin.image);
    } else {
      setImagePreview("");
    }
  }, [selectedAdmin]);

  const openModal = (admin = null) => {
    setSelectedAdmin(admin);
    setShowModal(true);
    if (admin?.image) {
      setImagePreview(admin?.image)
    }
  };

  const openConfirmModal = (action, data) => {
    setConfirmAction(action);
    setConfirmData(data);
    setConfirmModal(true);
  };

  const handleConfirm = async () => {
    try {
      if (confirmAction === "delete") {
        await deleteMentor(confirmData.id);
        toast.success("User deleted successfully");
      }

      if (confirmAction === "status") {
        await updateAdminStatus({ userId: confirmData.id });
        toast.success("Status updated successfully");
      }

      fetchAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Action failed");
    } finally {
      setConfirmModal(false);
      setConfirmAction(null);
      setConfirmData({});
    }
  };

  const AdminSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    image: Yup.string().required("Image is required"),
    description: Yup.string().required("Description is required"),
    qualification: Yup.string().required("Qualification is required"),
  });

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      if (selectedAdmin) {

        await updateMentor(values);
        toast.success("Mentor updated successfully");
      } else {
        await addMentor(values);
        toast.success("Mentor added successfully");
        resetForm();
      }
      fetchAdmins();
      setShowModal(false);
      setImagePreview("")
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save mentor");
    }
  };


  const handleFileUpload = async (e, fieldName, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const key = `uploads/${Date.now()}-${file.name}`;
      const res = await uploadFileToS3(file, key);

      if (res) {
        setImagePreview(URL.createObjectURL(file));
        setFieldValue(fieldName, res);
      }
    } catch (error) {
      toast.error("File upload failed");
      setImagePreview("");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 style={{ color: "#87C55F" }}>Mentors</h2>
        <Button
          style={{ backgroundColor: "#87C55F", border: "none" }}
          onClick={() => openModal()}
        >
          Add Mentor
        </Button>
      </div>

      <div className="pb-2 d-flex flex-wrap justify-content-between gap-2 align-items-end mb-4">
        <div className="flex-grow-1">
          <input
            type="text"
            style={{ borderRadius: "0.5rem", borderColor: "#87C55F" }}
            className="form-control w-100"
            placeholder="Search by name....."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {(search || date || endDate) && (
          <div>
            <button
              type="button"
              className="btn bg-danger text-white" // Fixed bootstrap class name (bg-danger instead of bg-red)
              style={{ borderRadius: "0.5rem" }}
              onClick={() => {
                setSearch("");
                setDate("");
                setEndDate("");
                setSortBy("");
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <table className="table table-striped align-middle shadow-sm">
          <thead className="table-light">
            <tr>
              <th>Sr No.</th>
              <th>Name</th>
              <th>Qualification</th>
              <th>Joined Date</th>
              <th style={{ textAlign: "center" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {admins?.length > 0 ? (
              admins.map((admin, index) => (
                <tr key={admin._id || index}>
                  <td>{currentPage * itemsPerPage + index + 1}</td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={admin?.image || `https://ui-avatars.com/api/?name=${admin?.name || "User"}&background=87C55F&color=fff`}
                        alt={admin?.name}
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "cover", // Ensures the image isn't stretched
                          borderRadius: "50%", // Makes the image a perfect circle
                          border: "1px solid #eaeaea"
                        }}
                      />
                      <span>{admin?.name}</span>
                    </div>
                  </td>
                  <td>{admin?.qualification}</td>
                  <td>
                    {new Date(admin?.createdAt).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                    {", "}
                    {new Date(admin?.createdAt).toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        variant="light"
                        id={`dropdown-basic-${index}`}
                        style={{ padding: "4px 8px" }}
                      >
                        {svg1}
                      </Dropdown.Toggle>

                      <Dropdown.Menu align="end">
                        <Dropdown.Item onClick={() => openModal(admin)}>
                          Edit
                        </Dropdown.Item>

                        <Dropdown.Item
                          onClick={() =>
                            openConfirmModal("delete", {
                              id: admin._id,
                              name: admin.name,
                            })
                          }
                        >
                          Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center"> {/* Updated colSpan to 6 */}
                  No mentors found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {admins?.length > 0 && (
        <div className="card-footer clearfix">
          <div className="d-flex justify-content-between align-items-center">
            <div className="dataTables_info" style={{ color: "#87C55F" }}>
              Showing {currentPage * itemsPerPage + 1} to{" "}
              {totalItems > (currentPage + 1) * itemsPerPage
                ? (currentPage + 1) * itemsPerPage
                : totalItems}{" "}
              of {totalItems} entries
            </div>
            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <Pagination
                pageCount={totalPages}
                pageValue={currentPage}
                setPage={setCurrentPage}
              />
            </div>
          </div>
        </div>
      )}

      <Modal size="xl" show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#87C55F" }}>
            {selectedAdmin ? "Edit Mentor" : "Add Mentor"}
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={
            selectedAdmin
              ? {
                mentorsId: selectedAdmin._id,
                name: selectedAdmin.name || "",
                image: selectedAdmin.image || "",
                qualification: selectedAdmin.qualification || "",
                description: selectedAdmin.description || "",
              }
              : {
                name: "",
                image: "",
                qualification: "",
                description: "",
              }
          }
          enableReinitialize
          validationSchema={AdminSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue }) => (
            <FormikForm>
              <Modal.Body>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <Field
                    name="name"
                    placeholder="ex. John Doe"
                    className={`form-control ${errors.name && touched.name ? "is-invalid" : ""
                      }`}
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="text-danger"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Qualification</label>
                  <Field
                    name="qualification"
                    type="text"
                    placeholder="ex. MBBS"
                    className={`form-control ${errors.qualification && touched.qualification
                        ? "is-invalid"
                        : ""
                      }`}
                  />
                  <ErrorMessage
                    name="qualification"
                    component="div"
                    className="text-danger"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <Field
                    name="description"
                    as="textarea"
                    row={7}
                    style={{ height: "100px" }}
                    placeholder="Brief description..."
                    className={`form-control ${errors.description && touched.description
                        ? "is-invalid"
                        : ""
                      }`}
                  />
                  <ErrorMessage
                    name="description"
                    component="div"
                    className="text-danger"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Image {uploading && "(Uploading...)"}</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    ref={fileInputRef}
                    onChange={(e) => handleFileUpload(e, "image", setFieldValue)}
                  />
                  <small className="text-muted d-block mt-1">
                    Max file size: 5MB. Supported formats: JPG, PNG, GIF, WebP
                  </small>

                  <ErrorMessage
                    name="image"
                    component="div"
                    className="text-danger"
                  />
                </div>

                {imagePreview && (
                  <div className="mb-3 text-center mt-3">
                    <div
                      style={{ display: "inline-block", position: "relative" }}
                    >
                      <img
                        src={imagePreview}
                        alt="preview"
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "cover",
                          borderRadius: "10px",
                          border: "1px solid #ddd",
                        }}
                      />

                      <button
                        type="button"
                        className="btn btn-danger btn-sm shadow"
                        style={{
                          position: "absolute",
                          top: "-8px",
                          right: "-8px",
                          borderRadius: "50%",
                          width: "24px",
                          height: "24px",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: "1",
                          fontSize: "16px",
                          fontWeight: "bold",
                        }}
                        onClick={() => {
                          setImagePreview("");
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                          }
                          setFieldValue("image", "");
                        }}
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  type="submit"
                  disabled={uploading}
                  style={{ backgroundColor: "#87C55F", border: "none" }}
                >
                  {selectedAdmin ? "Save Changes" : "Add Mentor"} {/* Fixed Button text */}
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      <Modal show={confirmModal} onHide={() => setConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {confirmAction === "delete"
              ? "Confirm Delete"
              : "Confirm Status Change"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {confirmAction === "delete" ? (
            <p>
              Are you sure you want to delete{" "}
              <strong>{confirmData.name}</strong>?
            </p>
          ) : (
            <p>
              Are you sure you want to{" "}
              <strong>{confirmData.isBlocked ? "Enable" : "Suspend"}</strong>{" "}
              <strong>{confirmData.name}</strong>?
            </p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            style={{ backgroundColor: "#87C55F", border: "none" }}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Mentor;